import { useContext, useEffect, useState } from "react";
import styles from "./styles/PosShoppingCart.module.css";
import POSDataContext from "../../../context/POSDataContext";
import DataContext from "../../../context/DataContext";
import { useNavigate, useParams } from "react-router-dom";
import Spinner from "../../../components/ui/spinner/Spinner";
import PosItemsPanel from "./PosItemsPanel";
import PosPayment from "./PosPayment";

/**
 * Shopping cart page
 */
const PosShoppingCart = () => {
    const {
        fetchShoppingCart, // {Function} - The function to fetch shopping cart data
        shoppingCartItems, // {Array} - The array of items in the shopping cart
        payments, // {Array} - The array of payment methods
        shoppingCartTotal, // {Float} - The total amount of the shopping cart
        isLoadingPos, // {Boolean} - Indicates if the data is loading
    } = useContext(POSDataContext);
    const {
        isServiceDataAvailable, // {Boolean} - Indicates if the service data is available
        isLoading, // {Boolean} - Indicates if the data is loading
        serviceData, // {Function} - Fetch service data
        currentBU, // {String} - The current business user
        JWTToken,
    } = useContext(DataContext);
    const { bUser } = useParams(); // Extracts 'bUser' parameter from the URL.

    const [showPayment, setShowPayment] = useState(false);
    const [isPaying, setIsPaying] = useState(false);
    const [notReadyView, setNotReadyView] = useState(true);

    const navigate = useNavigate(); // Hook for navigation in React Router.

    // Navigate back to home page if no business user or fetch shopping cart if so
    useEffect(() => {
        if (!currentBU) {
            navigate(`/${bUser}/pos`);
        } else {
            JWTToken && fetchShoppingCart();
        }
    }, [bUser, currentBU, JWTToken]);

    //Fetch service data if not avialable
    useEffect(() => {
        bUser &&
            JWTToken &&
            (!isServiceDataAvailable || bUser !== currentBU) &&
            serviceData(bUser);
    }, [bUser, isServiceDataAvailable, JWTToken]);

    // Check ready for viewing
    useEffect(() => {
        if (isServiceDataAvailable && (!isLoadingPos || !isLoading)) {
            const timer = setTimeout(() => {
                setNotReadyView(false);
            }, 50);

            // Cleanup function
            return () => {
                clearTimeout(timer);
            };
        }
    }, [isServiceDataAvailable, isLoadingPos, isLoading]);

    /**
     * Return the overall total from shopping cart with 2 fraction digits
     * @returns {Float} - The overall total from shopping cart
     */
    const getOverAllTotal = () => {
        return shoppingCartTotal;
    };

    /**
     * Return the amount that has been paid with 2 fraction digits
     * @returns {Float} - The ammount paid
     */
    const getOverAllPaid = () => {
        const total = payments.reduce(
            (sum, item) => sum + parseFloat(item?.amount),
            0
        );
        // return total;
        return Number(total || 0).toFixed(2);
    };
    /**
     * Return athe amount that is left to be paid with 2 fraction digits
     * @returns {Float} - The amount left to be paid
     */
    const calculateAmountToPay = () => {
        const total = getOverAllTotal() - getOverAllPaid();
        return Number(total || 0).toFixed(2);
    };

    return (
        <article
            className={`${styles.main_page_wrapper} main-page-container flex-container`}
        >
            {/* The spanner is shown in the PosItemPanel window when data is loading, 
            but in case no service data or there is a payment submitiing (not fetching data) it will show here */}
            {!isServiceDataAvailable || isPaying ? (
                <Spinner />
            ) : (
                <main
                    className={`${
                        styles.main_search_container
                    } transition-container 
                    ${
                        notReadyView
                            ? "transition-hidden"
                            : "transition-visible"
                    }  main-container flex-container`}
                >
                    {showPayment && (
                        <PosPayment
                            calculateAmountToPay={calculateAmountToPay}
                            setShowPayment={setShowPayment}
                            showPayment={showPayment}
                        />
                    )}
                    {/* Hide if a payment is submitting so that it is not conflicting with the spinner  */}
                    {!isPaying && (
                        <PosItemsPanel
                            calculateAmountToPay={calculateAmountToPay}
                            getOverAllTotal={getOverAllTotal}
                            shoppingCartItems={shoppingCartItems}
                            fetchShoppingCart={fetchShoppingCart}
                            setShowPayment={setShowPayment}
                            isPaying={isPaying}
                            setIsPaying={setIsPaying}
                        />
                    )}
                </main>
            )}
        </article>
    );
};

export default PosShoppingCart;
